import React, { useState, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import pageStyles from '../ManagementPages.module.css';
import useAxios from '../../services/AxiosConfig';
import { AddTimeIcon, TrashIcon } from '../../components/icons/Icons';
import { Form, FloatingLabel, Row, Col, Button, Container } from 'react-bootstrap';
import styles from './ReferenceCode.module.css';
import { updateRow, setRows, deleteRow} from './ReferenceCodeSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { showAlert } from '../../components/PerinHealthAlertSlice';
import { Paginator } from 'primereact/paginator';
import gridStyles from '../../components/PerinHealthGrid.module.css';
import { setDropdownKeyValues } from '../../slicers/DropdownSlice';

const AddReferenceCode = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const axios = useAxios(axiosConfig.envURL);
    const refCodeData = useSelector((state) => state.referenceCodes);
    const formRef = useRef(null);
    const [validated, setValidated] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const [newRows, setNewRows] = useState([]);
    const [first, setFirst] = useState(0);
    const [rowCount, setRowCount] = useState(15);

    const onPageChange = (event) => {
        setFirst(event.first);
        setRowCount(event.rows);
    };

    const newRow = {
        id: '',
        name: '',
        display: '',
        type: "organization",
        isActive: true
    };

    const handleNewRow = () => {
        setNewRows(prevRows => ([
            ...prevRows,
            newRow
        ]));
    };

    const removeRow = (index) => {
        const updatedRows = newRows.filter((_, i) => i !== index);
        setNewRows(updatedRows);
    };

    const updateNewRow = (name, value, index) => {
        const updatedRows = newRows.map((row, i) => 
            i === index ? { ...row, [name]: value} : row
        )
        setNewRows(updatedRows);
    };

    const onSaveHandler = () => {
        if (formRef.current) {
            let formIsInvalid = formRef.current.checkValidity() === false;
            setValidated(true); 
            if (formIsInvalid) {
                setErrorMessage('Each row must have a valid name and display');
                return;
            } else {
                const allRows = [...refCodeData.rows, ...newRows];
                let uniqueNames = new Set(allRows.map((row)=>row.name));
                let uniqueDisplayNames = new Set(allRows.map((row)=>row.display));
                if (uniqueNames.size !== allRows.length || uniqueDisplayNames.size !== allRows.length) {
                    setErrorMessage('Please ensure each row has unique name and display values');
                    return;
                } else {
                    setErrorMessage(null);
                    handleSubmit();
                }
            }
        }
    };

    const handleSubmit = async () => {
        const info = { 
            referenceCodeId: refCodeData.id,
            rows: refCodeData.rows,
            newRows: newRows.map((row) => ({
                name: row.name,
                display: row.display,
                type: row.type,
                isActive: row.isActive
            }))
        };
        try {
            const response = await axios.request({
                method: 'PUT',
                headers: { "Content-Type": "application/json" },
                url: `referenceCodes/${refCodeData.key}`,
                data: JSON.stringify(info),
            });
            if (response) {
                dispatch(
                    showAlert({
                        header: "Success",
                        message: "Reference codes updated successfully",
                        type: 'success'
                    })
                );
                if (response.data.data) {
                    dispatch(setDropdownKeyValues({key:refCodeData.key, values: response.data.data}));
                }
                navigate('/reference-code');
                
            }
        } catch (error) {
            console.log("error:" + error);
            dispatch(
                showAlert({
                    header: "Error",
                    message: "Error updating reference codes",
                    type: 'danger',
                    dismissable: true
                })
            );
        }
    };

    const paginatedRows = useMemo(() => {
        const allRows = [...newRows, ...refCodeData.rows];
        return allRows.slice(first, first + rowCount);
    }, [newRows, refCodeData.rows, first, rowCount]);

    return (
        <div className={pageStyles.MainCard}>
            <div className={pageStyles.ContentTitle}>{refCodeData.name}</div>
            <div className={pageStyles.HideScrollContainer}>
                <p className="body-2">Update information, or upload a file to replace all data.</p>
                <div style={{display:"flex", gap:"10px"}}>
                    <button className={styles.UploadButton}>
                        Upload from file
                    </button>
                </div>
                <hr></hr>
                <Form ref={formRef} noValidate validated={validated}>
                    <Container className={styles.FormContainer}>
                        <Row className={styles.HeaderRow}>
                            <Col xs={1}>ID</Col>
                            <Col>Name</Col>
                            <Col>Display</Col>
                            <Col xs={2}>Type</Col>
                            {!refCodeData.readOnly &&
                                <>
                                <Col xs={1}>isActive</Col>
                                <Col xs={1}></Col>
                                </>
                            }
                        </Row>
                        {paginatedRows.map((row, index) => {
                            const isNew = row.id === '';
                            return (
                            <Row className={styles.CodeRow} key={isNew ? `new-${index}` : `${row.id}.${index}`}>
                                <Col xs={1}>
                                    <Form.Group>
                                        <Form.Control
                                            className={styles.CodeInput}
                                            value={row.id}
                                            disabled
                                        />
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group>
                                        <Form.Control
                                            disabled={row.type==="standard"}
                                            className={styles.CodeInput}
                                            required
                                            value={row.name}
                                            onChange={(e) =>
                                                isNew
                                                    ? updateNewRow("name", e.target.value, index)
                                                    : dispatch(updateRow({ id: row.id, name: "name", value: e.target.value }))
                                            }
                                        />
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group>
                                        <Form.Control
                                            className={styles.CodeInput}
                                            required
                                            value={row.display}
                                            onChange={(e) =>
                                                isNew
                                                    ? updateNewRow("display", e.target.value, index)
                                                    : dispatch(updateRow({ id: row.id, name: "display", value: e.target.value }))
                                            }
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={2} style={{display:"flex", alignItems:"center"}}>
                                    <Form.Group>
                                        <div className={styles.ReadyOnlyRow}>
                                            {row.type}
                                        </div>
                                    </Form.Group>
                                </Col>
                                {!refCodeData.readOnly &&
                                    <>
                                    <Col xs={1} style={{display:"flex", alignItems:"center"}}>
                                        <Form.Group>
                                            <Form.Check
                                                type="switch"
                                                id="custom-switch"
                                                checked={row.isActive}
                                                onChange={(e) =>
                                                    isNew
                                                        ? updateNewRow("isActive", e.target.checked, index)
                                                        : dispatch(updateRow({ id: row.id, name: "isActive", value: e.target.checked }))
                                                }
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={1} style={{display:"flex", alignItems:"center"}} >
                                        {(isNew || row.type === "organization") && (
                                            <Button 
                                                className={styles.RemoveColButton} 
                                                onClick={() =>
                                                    isNew ?
                                                        removeRow(index)
                                                        : dispatch(deleteRow({ id: row.id }))
                                                }
                                            >
                                                <TrashIcon />
                                            </Button>
                                        )}
                                    </Col>
                                </>}
                            </Row>
                        )})}
                        <div className={styles.FormNavContainer}>
                                <span style={{ 
                                  fontFamily: 'Roboto',
                                  fontSize: '12px',
                                  fontWeight: 500,
                                  lineHeight: '16px',
                                  textAlign: 'left',
                                  color: '#25282B'
                                }}>Total Number: {newRows.length + refCodeData.rows.length}</span>
                            <Paginator
                                first={first}
                                rows={rowCount}
                                totalRecords={newRows.length + refCodeData.rows.length}
                                onPageChange={onPageChange}
                                rowsPerPageOptions={[15, 25, 50, 100]}
                                template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown "
                                className={gridStyles.customPaginator}
                            />
                            {!refCodeData.readOnly ?
                                <Button
                                    className={styles.AddColButton}
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={handleNewRow}
                                >
                                    <div style={{ display: "flex", alignItems: "center", columnGap: "10px" }}>
                                        <AddTimeIcon />
                                        Add Row
                                    </div>
                                </Button>
                                : <div></div>
                            }
                        </div>
                    </Container>
                </Form>
            </div>
            {!refCodeData.readOnly &&
                <div className={pageStyles.ButtonsFooterContainerWithError}>
                    {errorMessage &&
                        <div className="page-error">
                            <FontAwesomeIcon icon="fa-solid fa-triangle-exclamation" size="sm" style={{ color: "red" }} />
                            {errorMessage}
                        </div>
                    }
                    <div style={{ display: "flex", columnGap: "10px" }}>
                        <button
                            className={pageStyles.CreateButton}
                            onClick={onSaveHandler}
                        >
                            Update
                        </button>
                    </div>
                </div>
            }
        </div>
    );
}

export default AddReferenceCode;
